/**
 * Live Song Request real-time – Ably.
 * Subscribes to live_song:${branchId}.
 * See docs/ABLY_CUSTOMER_PORTAL.md.
 */
import { subscribeToChannel } from './ablyRealtime';
import { apiService } from './api';

const LIVE_SONG_EVENTS = [
  'live_song:config_updated',
  'live_song_request:created',
  'live_song_request:scheduled',
  'live_song_request:rejected',
  'live_song_request:current',
  'live_song_request:closed',
  'live_song_request_comment:pending',
  'live_song_request_comment:new',
  'live_song_request_comment:rejected',
] as const;

export class LiveSongRequestWebSocketService {
  private eventHandlers: Map<string, Set<Function>> = new Map();
  private unsubscribe: (() => void) | null = null;
  private currentBranchId: string | null = null;

  connect(branchId: string): void {
    if (this.currentBranchId === branchId && this.unsubscribe) return;
    this.disconnect();

    const { accessToken } = apiService.getTokens();
    if (!accessToken) {
      console.warn('[LiveSongRequestWS] No access token');
      return;
    }

    this.currentBranchId = branchId;
    const channelName = `live_song:${branchId}`;

    const emit = (event: string, data: any) => {
      this.eventHandlers.get(event)?.forEach((h) => {
        try {
          h(data);
        } catch (e) {
          console.error('[LiveSongRequestWS] handler error', e);
        }
      });
    };

    this.unsubscribe = subscribeToChannel(
      channelName,
      LIVE_SONG_EVENTS as unknown as readonly string[],
      emit
    );
  }

  leaveBranchRoom(_branchId: string): void {
    this.disconnect();
  }

  on(event: string, handler: Function): void {
    if (!this.eventHandlers.has(event)) this.eventHandlers.set(event, new Set());
    this.eventHandlers.get(event)!.add(handler);
  }

  off(event: string, handler: Function): void {
    this.eventHandlers.get(event)?.delete(handler);
  }

  isConnected(): boolean {
    return this.unsubscribe != null && this.currentBranchId != null;
  }

  disconnect(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this.currentBranchId = null;
    this.eventHandlers.clear();
  }
}

export const liveSongRequestWebSocketService = new LiveSongRequestWebSocketService();
